import type { UserStatus } from "@procuremate/shared";
import { clsx } from "clsx";
import { CircleCheck, CircleSlash, Mail } from "lucide-react";
import { useTranslation } from "react-i18next";

const STYLES: Record<UserStatus, { className: string; Icon: typeof Mail }> = {
  ACTIVE: { className: "bg-success-bg text-success", Icon: CircleCheck },
  INVITED: { className: "bg-warning-bg text-warning-fg", Icon: Mail },
  DISABLED: { className: "bg-danger-bg text-danger", Icon: CircleSlash },
};

/** Icon + text, never colour alone (BRAND.md accessibility rules). */
export function StatusBadge({ status }: { status: UserStatus }) {
  const { t } = useTranslation();
  const { className, Icon } = STYLES[status];
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold whitespace-nowrap",
        className,
      )}
    >
      <Icon aria-hidden="true" className="h-3.5 w-3.5" />
      {t(`admin.status.${status}`)}
    </span>
  );
}

/** Active / inactive marker for legal entities and business units. */
export function ActiveBadge({ active }: { active: boolean }) {
  const { t } = useTranslation();
  return <StatusBadgeLike active={active} label={active ? t("admin.org.active") : t("admin.org.inactive")} />;
}

function StatusBadgeLike({ active, label }: { active: boolean; label: string }) {
  const Icon = active ? CircleCheck : CircleSlash;
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold whitespace-nowrap",
        active ? "bg-success-bg text-success" : "bg-bg text-fg-muted",
      )}
    >
      <Icon aria-hidden="true" className="h-3.5 w-3.5" />
      {label}
    </span>
  );
}
